import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Config} from "../config/Config";
import {FireFrame} from "../components/FireFrame";
import {gsap} from "gsap";
import {Game} from "../Game";
import {FireFrameAnimName, SYMBOL} from "../enums/Enum";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {GameUtils} from "../utils/GameUtils";
import {LockFrame} from "../components/LockFrame";
import {Sound} from "../sound/Sound";


export class FireFrameView extends PIXI.Container implements StageLayer {
    private _lockFrameContainer: PIXI.Container;
    private _fireFrameContainer: PIXI.Container;
    private _splitFireContainer: PIXI.Container;
    private _xHoleFireFrameBlinkTLS: TimelineSprite;
    private _xMentalFireFrameBlinkTLSArr: PIXI.spine.Spine[] = [];

    constructor() {
        super();
        this.name = "FireFrameView";
        this.init();
    }

    public reset(): void {
        //TODO stuffs
    }

    public play(reelId: number, rowId: number, size: FireFrameAnimName, lockSize: number, shouldPlayXHoleAnticipation: boolean): gsap.core.Timeline {
        const childIndex: number = (reelId * 3) + rowId - Math.floor(reelId / 2);
        const fireFrame: FireFrame = (this._fireFrameContainer.children[childIndex] as FireFrame);
        const tl: gsap.core.Timeline = fireFrame.play(size);
        tl.add(this.handleLock(true, reelId, rowId, lockSize), 0);
        if (shouldPlayXHoleAnticipation) {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.X_HOLE_ANTICIPATION);
                this.playXHoleBlink();
            });
        }
        return tl;
    }

    public stop(reelId: number, rowId: number, parts: number, lockSize: number): gsap.core.Timeline {
        const childIndex: number = (reelId * 3) + rowId - Math.floor(reelId / 2);
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const fireFrame: FireFrame = (this._fireFrameContainer.children[childIndex] as FireFrame);
        tl.add([
            fireFrame.stop(),
            this.handleLock(false, reelId, rowId, lockSize),
        ], 0);
        const symSize: number = (reelId === 2 && rowId === 0) ? 99 : 198;
        const size: number = symSize / (parts);
        if (reelId === 2 && rowId === 1 && Game.model.shouldPlayXHoleAnticipation()) {
            tl.add(() => {
                Game.soundPlayer.stopSound(Sound.X_HOLE_ANTICIPATION);
                if (Game.model.gameData.evaluatedArea[2][1] === SYMBOL.X_HOLE) {
                    Game.soundPlayer.playSound(Sound.X_HOLE_LAND_ACTIVE);
                } else {
                    Game.soundPlayer.playSound(Sound.X_HOLE_MISS);
                }
                this.stopXHoleBlink();
            }, 0);
            tl.add(GameUtils.shake(7, 7, 100, 1, 0.3), 0)
        }
        if (Game.model.xMentalPos.x === reelId) {
            tl.add(() => {
                this.stopXMentalAnticipation(reelId, rowId);
            });
        }

        if (Game.model.gameData.reels[reelId][rowId] !== SYMBOL.X_HOLE && Game.model.gameData.reels[reelId][rowId] !== SYMBOL.X_MENTAL) {
            for (let part: number = 1; part < parts; part++) {
                const symbolSplitFire: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("split_effect/split_effect_000"));
                const posY: number = (fireFrame.position.y - (symSize * 0.5)) + (size * part) + 6;
                symbolSplitFire.anchor.set(0.5);
                symbolSplitFire.position.set(fireFrame.position.x - 11, posY);
                symbolSplitFire.blendMode = PIXI.BLEND_MODES.ADD;
                symbolSplitFire.scale.set(2);
                tl.add([
                    () => {
                        this._splitFireContainer.addChild(symbolSplitFire);
                    },
                    symbolSplitFire.getAnimationAutoShowHide(true, true, void (0), void (0), () => {
                        symbolSplitFire.parent?.removeChild(symbolSplitFire);
                    })], 0);
            }
        }
        return tl;
    }

    public playXMentalAnticipation(reelId: number, rowId: number, isFireReel: boolean): void {
        const pos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xMentalAnticipation"));
        spine.position.set(pos.x, pos.y);
        this.addChild(spine);
        if (isFireReel) {
            let reelTotalHeight: number = 3;
            let posY: number = 0;
            if (reelId === 2) {
                reelTotalHeight = 2.5;
                posY += Config.SYMBOL_HEIGHT_HALF * 0.5;
            } else if (reelId === 1 || reelId === 3) {
                reelTotalHeight = 2;
                posY -= Config.SYMBOL_HEIGHT_HALF;
            }
            spine.height = 198 * reelTotalHeight * 0.854 * 1.75;
            spine.y += posY;
        } else if (reelId === 2 && rowId === 0) {
            spine.scale.y = 0.5;
        }
        spine.name = "XMentalAnticipationTLS_" + reelId + "_" + rowId;
        this._xMentalFireFrameBlinkTLSArr[rowId] = spine;
        spine.state.setAnimation(0, "animation", true);
    }

    public stopXMentalAnticipation(reelId: number, rowId: number): void {
        if (this._xMentalFireFrameBlinkTLSArr.length) {
            const spine: PIXI.spine.Spine = this._xMentalFireFrameBlinkTLSArr[rowId];
            if (spine) {
                spine.state.clearTracks();
                spine.parent?.removeChild(spine);
                delete this._xMentalFireFrameBlinkTLSArr[rowId];
            }
        }
    }

    private showLock(reelId: number, rowId: number, show: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (Game.model.stickyS2[reelId] !== rowId) {
            const lockChildIndex: number = (reelId * 3) + rowId - Math.floor(reelId / 2);
            const currentLock: LockFrame = this._lockFrameContainer.children[lockChildIndex] as LockFrame;
            tl.add(currentLock.showHide(show), 0);
            if (show) {
                tl.add(currentLock.glowFadeIn(), 0);
                tl.add(currentLock.glowFadeOut());
            }
        }
        return tl;
    }

    private handleLock(show: boolean, reelId: number, rowId: number, lockSize: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (lockSize > 1) {
            if (lockSize === 3) {
                for (let rowForReelId: number = 0; rowForReelId < Config.SYMBOLS_IN_REEL[reelId]; rowForReelId++) {
                    tl.add(this.showLock(reelId, rowForReelId, show), 0);
                }
            } else {
                tl.add(this.showLock(reelId, rowId, show));
            }
        }
        return tl;
    }

    private playXHoleBlink(): void {
        this._xHoleFireFrameBlinkTLS.show();
        this._xHoleFireFrameBlinkTLS.playLoop();
    }

    private stopXHoleBlink(): void {
        this._xHoleFireFrameBlinkTLS.stopLoop();
        this._xHoleFireFrameBlinkTLS.hide();
    }

    private createXHoleFireBlinkAnim(): void {
        const pos: PIXI.Point = GameUtils.getSymbolPos(2, 1);
        this._xHoleFireFrameBlinkTLS = GameUtils.createTimelineSprite("xhole_blink/xhole_blink_000", 30, pos, true);
        this._xHoleFireFrameBlinkTLS.scale.set(2, -2);
        this._xHoleFireFrameBlinkTLS.blendMode = PIXI.BLEND_MODES.ADD;
        this.addChild(this._xHoleFireFrameBlinkTLS);
        this._xHoleFireFrameBlinkTLS.hide();
    }

    private init(): void {
        this._lockFrameContainer = new PIXI.Container();
        this._lockFrameContainer.name = "LOCK_CONTAINER";
        this._fireFrameContainer = new PIXI.Container();
        this._fireFrameContainer.name = "FIRE_FRAME_CONTAINER";
        for (let reelId: number = 0; reelId < 5; reelId++) {
            for (let rowId: number = 0; rowId < Config.SYMBOLS_IN_REEL[reelId]; rowId++) {
                const fireFrame: FireFrame = new FireFrame(reelId, rowId);
                this._fireFrameContainer.addChild(fireFrame);
                this._lockFrameContainer.addChild(new LockFrame(reelId, rowId));
            }
        }
        this._splitFireContainer = new PIXI.Container();
        this._splitFireContainer.name = "SPLIT_FIRE_CONTAINER";
        this.addChild(this._lockFrameContainer, this._fireFrameContainer, this._splitFireContainer);
        this.createXHoleFireBlinkAnim();
        this.position.set(0, Config.MARGIN_TOP);
    }

}